<script setup lang="ts">
    import { getConfig, klinikenApi } from "@/api";
    import { openDialog } from "@/utils";
    import { debounce } from "lodash";
    import { CreateElement, ref } from "vue";
    import VSelect from "vue-select";
    import SearchIcon from "@/assets/search.svg";

    interface Material {
        id: string;
        artikelnummer: string;
        lokalt_artikelnummer?: string;
        namn: string;
    }

    interface Grupp {
        id: string;
        gruppnummer: string;
        namn: string;
        material: {
            antal: number;
            material: Material;
        }[];
    }

    interface Option {
        value: Material | Grupp;
        namn: string;
        label: string;
        type: string;
    }

    interface Data {
        material: Material[];
        grupper: Grupp[];
    }

    const emit = defineEmits<{
        (
            event: "selected",
            selected: {
                type: string;
                value: Material | Grupp;
            }
        ): void;
        (event: "error", error: unknown): void;
    }>();

    withDefaults(
        defineProps<{
            id?: string;
        }>(),
        {
            id: "material-scanner",
        }
    );

    const getEmptyCache = (): Data => {
        return {
            material: [],
            grupper: [],
        };
    };

    const selected = ref<Option | null>(null);
    const cachedResult = ref<Data>(getEmptyCache());
    const selectRef = ref<{
        open: boolean;
        search: string;
    } | null>(null);

    const OpenIndicator = {
        render: (createElement: CreateElement) =>
            createElement("img", {
                attrs: { src: SearchIcon },
                class: { indicator: true },
            }),
    };

    const createGruppOption = ({ grupp }: { grupp: Grupp }) => {
        return {
            label: `${grupp.namn} (ink. ${grupp.material.length} material)`,
            namn: grupp.namn,
            type: "grupp",
            value: grupp,
        };
    };

    const createMaterialOption = ({ material }: { material: Material }) => {
        return {
            label: material.namn,
            namn: material.namn,
            type: "material",
            value: material,
        };
    };

    const convertCachedToOptions = (): Option[] => [
        ...cachedResult.value.material.map(function mapMaterial(singleMaterial) {
            return createMaterialOption({ material: singleMaterial });
        }),
        ...cachedResult.value.grupper.map(function mapGrupper(grupp) {
            return createGruppOption({ grupp });
        }),
    ];

    const emitSelect = ({ option }: { option: Option }) => {
        emit("selected", {
            type: option.type,
            value: option.value,
        });

        cachedResult.value = getEmptyCache();
        selected.value = null;
    };

    const emitError = ({ error }: { error: unknown }) => {
        cachedResult.value = getEmptyCache();

        emit("error", error);
    };

    const onSelected = (option: Option) => {
        if (option.type === "grupp") {
            const grupp = option.value as Grupp;

            openDialog({
                type: "information",
                description: `Vill du lägga till ${grupp.material.length} artiklar?`,
                lines: grupp.material.map(function mapLines({ material, antal }) {
                    return `${antal} st ${material.namn} ${material.artikelnummer}`;
                }),
                buttons: [
                    {
                        title: "Avbryt",
                        type: "secondary",
                        action: () => {
                            selected.value = null;
                        },
                    },
                    {
                        title: "Ja, lägg till",
                        type: "primary",
                        action: () => {
                            emitSelect({ option });
                        },
                    },
                ],
            });

            return;
        }

        emitSelect({ option });
    };

    const selectOnlyOption = ({ option, loading }: { option: Option; loading: (isLoading: boolean) => void }) => {
        if (selectRef.value) {
            selectRef.value.open = false;
            selectRef.value.search = "";
        }

        onSelected(option);
    };

    const onSearch = async (newKod: string, loading: (isLoading: boolean) => void) => {
        if (newKod === "") {
            cachedResult.value = getEmptyCache();
            loading(false);

            return;
        }

        await retrieveMaterial({
            newKod,
            loading,
        });
    };

    const retrieveMaterial = debounce(
        async ({ newKod, loading }: { newKod: string; loading: (isLoading: boolean) => void }) => {
            try {
                const { data } = await klinikenApi.get<Data>(
                    "/operation/material/scanner/",
                    getConfig({
                        params: {
                            kod: newKod,
                            ordering: "artikelnummer",
                        },
                    })
                );

                if (data.material.length === 1 && data.grupper.length === 0) {
                    selectOnlyOption({
                        loading,
                        option: createMaterialOption({ material: data.material[0] }),
                    });

                    return;
                }

                if (data.grupper.length === 1 && data.material.length === 0) {
                    selectOnlyOption({
                        loading,
                        option: createGruppOption({ grupp: data.grupper[0] }),
                    });

                    return;
                }

                cachedResult.value = data;
            } catch (error) {
                emitError({ error: error });
            } finally {
                loading?.(false);
            }
        },
        1000
    );

    const getOptionLabel = (option: Option) => {
        return option.label;
    };
</script>

<template>
    <div class="material-scanner">
        <label :for="id" class="label">Sök efter material</label>
        <v-select
            :id="id"
            ref="selectRef"
            @input="onSearch"
            :value="selected"
            :filterable="false"
            :options="convertCachedToOptions()"
            :get-option-label="getOptionLabel"
            @option:selected="onSelected"
            @search="
                (search, loading) => {
                    loading(true);
                    onSearch(search, loading);
                }
            "
            :components="{ OpenIndicator }"
        >
            <template #no-options="{ search }">
                <span v-if="search !== ''"> Inga resultat </span>
                <span v-else> Fyll i fältet för att söka på material </span>
            </template>
        </v-select>
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables.scss";

    .material-scanner {
        ::v-deep .v-select.vs--open {
            .indicator {
                transform: scale(var(--vs-controls-size));
            }
        }

        .label {
            color: $color-label-grey;
        }
    }
</style>
